import {Inject, Injectable, computed, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';

import {Observable, throwError} from 'rxjs';

import {catchError, map, tap} from 'rxjs/operators';

import {FormFactory} from '@core/modules/forms/services/form.factory';

import {IToken} from '../types/token.model';
import {AuthLogin} from '../types/auth-login';
import {RegisterInfo} from '../types/register.model';
import {APIResponse} from '@core/helpers/types/api-response';
import {ApiKey} from '../types/api-key';
import {UserView} from '@shared/modules/users/types/user-view.model';

import {User, UserRole} from '@shared/modules/users/types/user';

import {UserStore} from './stores/user.store';
import {TokenStore} from './stores/token.store';
import {AccountStore} from '@core/modules/auth/services/stores/account.store';
import {AccountPreview, WhoAmIDto} from '@core/modules/auth/types/whoami';
import {Account} from '@shared/modules/accounts/types';
import {CookieStore} from '@core/modules/storage';
import {ConfigService} from '@core/config/config.service';
import {ThemeService} from '@core/modules/platform';

const IMPERSONATION_STORAGE_KEY = 'impersonated_account';

/**
 * Provides all Http methods related to User Authentication
 */
@Injectable({providedIn: 'root'})
export class AuthService {
	readonly currentUser = signal<UserView | undefined>(
		new UserView(this.userStore.getUser())
	);
	readonly isLoggedIn = computed(() => !!this.currentUser()?.uuid);

	private readonly impersonatedAccount = signal<Partial<Account> | undefined>(
		undefined
	);
	readonly isImpersonating = computed(() => !!this.impersonatedAccount());

	constructor(
		private router: Router,
		private http: HttpClient,
		private tokenStore: TokenStore,
		private userStore: UserStore,
		private cookie: CookieStore,
		private accountsStore: AccountStore,
		private route: ActivatedRoute,
		private forms: FormFactory,
		private configService: ConfigService,
		private themeService: ThemeService
	) {
		this.restoreImpersonationFromStorage();
	}

	private restoreImpersonationFromStorage(): void {
		try {
			const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
			if (!raw) return;
			const account = JSON.parse(raw) as Partial<Account>;
			if (!account?.uuid) return;
			this.impersonatedAccount.set(account);
			this.applyImpersonationOverlay(account);
		} catch {
			sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
		}
	}

	private applyImpersonationOverlay(account: Partial<Account>): void {
		const stored = this.userStore.getUser();
		if (!stored) return;
		const adminRights = (account?.accountRoles?.admin ?? []).map(code => ({
			code,
			name: code,
		}));
		const accountWithRole = {
			...account,
			accountRole: account?.accountRole ?? {code: 'admin', rights: adminRights},
		};
		const nameParts = (account.name ?? '').trim().split(/\s+/);
		const overlaid = {
			...stored,
			roles: [UserRole.USER],
			firstName: nameParts[0] ?? undefined,
			lastName: nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined,
			email: account.email ?? stored.email,
			account: accountWithRole,
		} as any;
		this.currentUser.set(new UserView(overlaid));
	}

	enterImpersonation(account: Partial<Account>, redirectTo?: string): void {
		if (!account?.uuid) return;
		sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(account));
		this.impersonatedAccount.set(account);
		this.applyImpersonationOverlay(account);
		this.cookie.delete('videos-library');
		sessionStorage.removeItem('navigation-context');
		if (redirectTo) window.location.assign(redirectTo);
	}

	exitImpersonation(redirectTo?: string): void {
		if (!this.isImpersonating()) return;
		const previousUuid = this.impersonatedAccount()?.uuid;
		sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
		this.impersonatedAccount.set(undefined);
		const stored = this.userStore.getUser();
		this.currentUser.set(stored ? new UserView(stored) : undefined);
		this.cookie.delete('videos-library');
		sessionStorage.removeItem('navigation-context');
		const target =
			redirectTo ??
			(previousUuid
				? `/vidmizer-board/account/${previousUuid}`
				: '/vidmizer-board');
		window.location.assign(target);
	}

	/**
	 * Tries to register the user with given information:
	 * - if the request is successful, the user must confirm his email, so no actions follow.
	 * - If an error occurred, the LoginComponent will take car of displaying the error messages.
	 */
	signUp = (info: RegisterInfo): Observable<void> =>
		this.http
			.post<void>(this.configService.getApiUrl() + '/register', info)
			.pipe(
				tap(
					() => this.forms.handleSuccessMessages('HTTP_SUCCESS.REGISTER'),
					(err: any) => this.forms.handleErrorMessages(err)
				)
			);

	/**
	 * Tries to authenticate the user with given credentials:
	 * - if the request is successful, the session start and user is redirect to his dashboard
	 * - If an error occurred, the LoginComponent will take car of displaying the error messages.
	 */
	logIn = (
		form: AuthLogin
	): Observable<IToken> => // Log and update current User Subject
		this.http
			.post<IToken>(this.configService.getApiUrl() + '/login', form)
			.pipe(
				catchError(async err => {
					this.forms.handleErrorMessages(err);
					return err;
				})
			)
			.pipe(
				tap(
					token => {
						if (token.code === 200) {
							this.onAuthenticationSuccess(token);
						}
					},
					err => console.error(err)
				)
			);

	googleAuth = () => {
		window.location.href = `${this.configService.getApiUrl()}/auth/google`;
	};
	magicLinkAuth = (payload: {email: string}) =>
		this.http
			.post<IToken>(this.configService.getApiUrl() + '/magic-link', payload)
			.pipe(catchError(err => err));

	verifyCode = (code: string): Observable<IToken> =>
		this.http
			.post<IToken>(this.configService.getApiUrl() + '/auth/verify', {code})
			.pipe(
				tap(token => {
					if (token.code === 200) {
						this.onAuthenticationSuccess(token);
					}
				}),
				catchError(err => {
					return throwError(() => err);
				})
			);

	/**
	 * Clears user's session and redirect to homepage
	 */
	logOut = (): void => {
		this.tokenStore.clearToken();
		this.userStore.clearUser();
		this.cookie.deleteAll();
		sessionStorage.clear();
		this.themeService.reset();
		this.impersonatedAccount.set(undefined);
		this.accountsStore.clearAccounts();
		this.currentUser.set(undefined);
		this.router.navigate(['/']);
	};

	/**
	 * Send request to reset password of user depending on given user
	 * @param email user's public identifier
	 */
	resetPassword = (email: string): Observable<any> =>
		this.http
			.post<any>(this.configService.getApiUrl() + '/users/forgotten-password', {
				email,
			})
			.pipe(
				map(() =>
					this.router
						.navigate(['/'])
						.catch(err => console.error('Failed to redirect', err))
				)
			);

	/**
	 *
	 * @param form
	 * @param token
	 * @returns
	 */
	updatePassword = (
		form: {password: string; _password: string},
		token: string
	): Observable<any> =>
		this.http.put<void>(
			`${this.configService.getApiUrl()}/users/change-password`,
			form,
			{
				headers: {Authorization: 'Bearer ' + token},
			}
		);

	/**
	 * Retrieves current user information based on stored token
	 */
	whoami = (): Observable<WhoAmIDto> =>
		this.http
			.get<APIResponse<WhoAmIDto>>(this.configService.getApiUrl() + '/me')
			.pipe(map(res => res.data));

	/**
	 * Determines if current user has given Role
	 */
	hasRole(role: UserRole | string, user?: UserView): boolean {
		const currentRoles = user ? user.roles : this.currentUser()?.roles;
		return !currentRoles
			? false
			: currentRoles.filter(
					(currentRole: string) => currentRole === role.toString()
			  ).length !== 0;
	}

	getApiKey = (account_uuid?: string): Observable<string> =>
		this.http
			.patch<APIResponse<ApiKey>>(
				this.configService.getApiUrl() +
					'/accounts/' +
					account_uuid +
					'/api-key',
				{}
			)
			.pipe(map(res => res.data.apiKey));

	updateCurrentAccount(updatedAccount: Partial<AccountPreview>) {
		if (this.isImpersonating()) return;
		const user = this.currentUser();
		if (user) {
			const previousAccount = this.currentUser()?.account?.uuid;
			const account = user.account;
			user.account = {...account, ...updatedAccount};
			this.currentUser.set(new UserView(this.userStore.saveUser(user)));
			if (!previousAccount || previousAccount !== updatedAccount.uuid) {
				this.cookie.delete('videos-library');
				location.reload();
			}
		}
	}

	updateCurrentUserInformation(input: User | UserView) {
		if (this.isImpersonating()) return;
		const user = {...this.currentUser(), ...input};
		if (user?.uuid) {
			this.currentUser.set(new UserView(this.userStore.saveUser(user)));
		}
	}

	isAdminType = () =>
		this.hasRole(UserRole.DEV, this.currentUser()) ||
		this.hasRole(UserRole.VIDMIZER, this.currentUser());

	isUserType = () => this.hasRole(UserRole.USER, this.currentUser());

	isPlayerActive = () =>
		this.currentUser()?.account?.accountRole?.rights.some(
			obj => obj.name === 'player'
		);

	onAuthenticationSuccess = (token: IToken) => {
		sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
		this.impersonatedAccount.set(undefined);
		if (!token.token) return;
		this.tokenStore.saveToken(token);
		this.whoami()
			.pipe(
				tap(res => {
					if (res) {
						this.accountsStore.saveAccounts(res.accounts);
						this.currentUser.set(
							new UserView(this.userStore.saveUser(res.user))
						);
					}
				})
			)
			.pipe(
				map(() =>
					this.router
						.navigate(['/app/library'])
						.catch(err => console.error('Failed to redirect', err))
				)
			)
			.subscribe();
	};

	InviteUser = (payload: {email: string}): Observable<void> =>
		this.http
			.post<void>(this.configService.getApiUrl() + '/auth/invite', payload)
			.pipe(
				tap(
					() => this.forms.handleSuccessMessages('HTTP_SUCCESS.REGISTER'),
					(err: any) => this.forms.handleErrorMessages(err)
				)
			);
}
